import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import {
  type AccountSummary,
  type DerivedStatus,
  deriveAccountIssue,
  describeStatusReason,
} from "@/lib/accounts";
import {
  useInboxState,
  useSetInboxEnabled,
} from "@/lib/inbox";
import {
  useMaintenanceStatus,
  useSetMaintenanceEnabled,
  useRestartWarmup,
  useSkipWarmup,
  useStartWarmup,
  useWarmupSchedule,
} from "@/lib/warmup";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { ConfirmDialog } from "@/components/ui/dialog";
import { formatRelative, formatDateTime } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { KpiTile, Stat, StateRow } from "@/components/accounts/shared";

// Same bands as the list's Health filter and its tooltip: high ≥70, mid 40–69,
// low <40 — so a "High" account never shows up amber here.
function healthTone(score: number | undefined): "default" | "success" | "warning" | "danger" {
  if (score == null) return "default";
  if (score >= 70) return "success";
  if (score >= 40) return "warning";
  return "danger";
}

export function AccountOverviewTab({
  account,
  displayStatus,
}: {
  account: AccountSummary;
  displayStatus: DerivedStatus;
}) {
  const { t } = useTranslation();

  const ageDays = account.seller_account_age_days;
  const cost = account.purchase_cost_usd;

  return (
    <div className="space-y-5">
      <AttentionAlert account={account} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile
          label={t("accounts.detail.invites_sent")}
          value={account.total_invites_sent}
        />
        <KpiTile
          label={t("accounts.detail.total_floodwaits")}
          value={account.total_floodwaits}
          tone={account.total_floodwaits > 0 ? "warning" : "default"}
        />
        <KpiTile
          label={t("accounts.kpi.age")}
          value={ageDays != null ? t("accounts.kpi.age_days", { count: ageDays }) : "—"}
        />
        <KpiTile
          label={t("accounts.kpi.cost")}
          value={cost ? `$${cost}` : "—"}
        />
        <KpiTile
          label={t("accounts.health.title")}
          value={account.health_score != null ? `${account.health_score}/100` : "—"}
          tone={healthTone(account.health_score)}
        />
      </div>

      {account.health_breakdown && (
        <div className="-mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
          <span>
            {t("accounts.health.bans")}: {account.health_breakdown.bans}/30
          </span>
          <span>
            {t("accounts.health.profile")}: {account.health_breakdown.profile}/20
          </span>
          <span>
            {t("accounts.health.activity")}: {account.health_breakdown.activity}/20
          </span>
          <span>
            {t("accounts.health.age")}: {account.health_breakdown.age}/15
          </span>
          <span>
            {t("accounts.health.session")}: {account.health_breakdown.session}/15
          </span>
        </div>
      )}

      <WarmupSection account={account} />

      {/* State list */}
      <Card>
        <CardContent className="divide-y divide-border pt-2">
          <StateRow label={t("accounts.col.status")}>
            <StatusBadge
              domain="account"
              value={displayStatus}
              label={t(`accounts.status.${displayStatus}`)}
              dot
            />
          </StateRow>
          {account.status_reason && (
            <StateRow label={t("accounts.detail.status_reason")}>
              <span className="text-xs text-fg-muted">
                {describeStatusReason(account.status_reason, t)}
              </span>
            </StateRow>
          )}
          <StateRow label={t("accounts.col.role")}>
            {account.role === "demoted_scraper" ? (
              <StatusBadge
                domain="account"
                value="paused"
                label={t("accounts.role.demoted_scraper")}
              />
            ) : (
              <span>{t(`accounts.role.${account.role}`)}</span>
            )}
          </StateRow>
          <StateRow label={t("accounts.col.proxy")}>
            {account.proxy_label ? (
              <span className="flex items-center gap-1.5 font-mono text-xs">
                <span
                  aria-hidden="true"
                  className={
                    account.proxy_is_alive === false
                      ? "h-2 w-2 rounded-full bg-danger"
                      : "h-2 w-2 rounded-full bg-success"
                  }
                />
                {account.proxy_label}
              </span>
            ) : (
              <span className="text-fg-muted">—</span>
            )}
          </StateRow>
          <StateRow label={t("accounts.col.last_check")}>
            <span className="text-fg-muted">
              {account.last_check_at ? formatRelative(account.last_check_at) : "—"}
            </span>
          </StateRow>
          <SpamBotRows account={account} />
          <MaintenanceRow accountId={account.id} />
          <InboxRow accountId={account.id} />
          <StateRow label={t("accounts.own_chats.title")}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // In-app navigation: the router listens for popstate.
                window.history.pushState(null, "", `/own-chats?account=${account.id}`);
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
            >
              {t("accounts.own_chats.open")}
            </Button>
          </StateRow>
        </CardContent>
      </Card>

      {/* Technical facts */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-x-4 gap-y-3 pt-6 sm:grid-cols-3">
          <Stat label={t("accounts.detail.tg_user_id")} value={account.tg_user_id ?? "—"} mono />
          <Stat
            label={t("accounts.detail.api_credential")}
            value={account.api_credential_label ?? "—"}
          />
          <Stat label={t("accounts.detail.phone")} value={account.phone ?? "—"} mono />
          <Stat
            label={t("accounts.detail.first_name")}
            value={[account.first_name, account.last_name].filter(Boolean).join(" ") || "—"}
          />
          <Stat
            label={t("accounts.detail.total_requests")}
            value={account.total_requests}
            mono
          />
          <Stat
            label={t("accounts.detail.last_used_at")}
            value={account.last_used_at ? formatRelative(account.last_used_at) : "—"}
          />
        </CardContent>
      </Card>

      {/* Source summary */}
      {(account.source_label || account.source_url || account.country_code) && (
        <Card>
          <CardContent className="divide-y divide-border pt-2">
            {account.source_label && (
              <StateRow label={t("accounts.field.source_label")}>
                {account.source_url ? (
                  <a
                    href={account.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline"
                  >
                    {account.source_label}
                  </a>
                ) : (
                  account.source_label
                )}
              </StateRow>
            )}
            {account.country_code && (
              <StateRow label={t("accounts.field.country_code")}>
                {account.country_code}
              </StateRow>
            )}
            <StateRow label={t("accounts.detail.created_at")}>
              <span className="text-fg-muted">{formatRelative(account.created_at)}</span>
            </StateRow>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * The one current problem (same classifier as the list's "With issues"), with
 * what it means and what to do. The raw error of the last connect rides along
 * — it used to sit in its own banner at the bottom of the tab.
 */
function AttentionAlert({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const issue = deriveAccountIssue(account);
  if (issue === null) return null;

  let title: string;
  let body: string;
  switch (issue) {
    case "proxy":
      if (account.status_reason === "proxy_required") {
        // The import's verify refused to connect without a proxy. Assigning
        // one now doesn't restart it (and Test is gated on a never-checked
        // account), so the way out is a fresh import.
        title = t("accounts.attention.proxy_reimport.title");
        body = t("accounts.attention.proxy_reimport.body");
      } else if (account.proxy_id === null) {
        title = t("accounts.attention.proxy_none.title");
        body = t("accounts.attention.proxy_none.body");
      } else {
        title = t("accounts.attention.proxy.title", { label: account.proxy_label });
        body = t("accounts.attention.proxy.body");
      }
      break;
    case "flood_wait":
      // A lapsed "until" only lingers until the cron flips the status back;
      // don't print a time that has already passed.
      title =
        account.flood_wait_until && Date.parse(account.flood_wait_until) > Date.now()
          ? t("accounts.attention.flood_wait.title", {
              time: formatDateTime(account.flood_wait_until),
            })
          : t("accounts.attention.flood_wait.title_open");
      body = t("accounts.attention.flood_wait.body");
      break;
    case "spam_limit":
      if (account.spam_state === "limited") {
        title = account.spam_limited_until
          ? t("accounts.attention.spam_limit.title_until", {
              time: formatDateTime(account.spam_limited_until),
            })
          : t("accounts.attention.spam_limit.title");
        // @SpamBot says why; say it in plain words when we recognise it.
        body = account.spam_reason
          ? t(`accounts.attention.spam_limit.reason_${account.spam_reason}`)
          : t("accounts.spam_limited_tooltip");
      } else {
        // PeerFlood cooldown (inPeerFloodCooldown): the only other way here.
        title = t("accounts.attention.peer_flood_cooldown.title", {
          time: formatDateTime(account.peer_flood_until ?? ""),
        });
        body = t("accounts.attention.peer_flood_cooldown.body");
      }
      break;
    case "other":
      if (account.is_peer_flood_permanent) {
        title = t("accounts.peer_flood_perm");
        body = t("accounts.attention.peer_flood.body");
      } else {
        title = t("accounts.attention.last_check.title");
        body = t("accounts.attention.last_check.body");
      }
      break;
    case "session":
      title = t("accounts.attention.session.title");
      // The reason the verify recorded tells revoked, duplicated and
      // password-locked sessions apart — each has a different fix.
      body =
        account.status_reason === "auth_duplicated"
          ? t("accounts.attention.session.body_duplicated")
          : account.status_reason === "password_needed"
            ? t("accounts.attention.session.body_password")
            : t("accounts.attention.session.body");
      break;
    default:
      title = t(`accounts.attention.${issue}.title`);
      body = t(`accounts.attention.${issue}.body`);
  }

  return (
    <Alert
      tone={issue === "flood_wait" || issue === "spam_limit" ? "warning" : "danger"}
      title={
        <>
          <span className="block text-xs font-medium uppercase tracking-wide text-fg-muted">
            {t("accounts.attention.label")}
          </span>
          {title}
        </>
      }
    >
      <p>{body}</p>
      {account.last_check_error && (
        <code className="mt-2 block break-all text-xs">{account.last_check_error}</code>
      )}
    </Alert>
  );
}

/** What @SpamBot last said about the account, and its words in full. */
function SpamBotRows({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const limited = account.spam_state === "limited";
  const verdict = limited
    ? account.spam_limited_until
      ? t("accounts.spambot.limited_until", {
          time: formatDateTime(account.spam_limited_until),
        })
      : t("accounts.spambot.limited")
    : account.spam_state === "clean" && account.spam_checked_at
      ? t("accounts.spambot.clean")
      : t("accounts.spambot.unknown");
  return (
    <>
      <StateRow label={t("accounts.spambot.label")}>
        <span className="flex flex-col items-end">
          <span className={limited ? "text-danger" : undefined}>{verdict}</span>
          {account.spam_checked_at && (
            <span className="text-xs text-fg-muted">
              {t("accounts.spambot.checked", { when: formatRelative(account.spam_checked_at) })}
            </span>
          )}
        </span>
      </StateRow>
      {account.spam_reply && (
        <details className="py-2 text-sm">
          <summary className="cursor-pointer text-fg-muted hover:text-fg-primary">
            {t("accounts.spambot.show_reply")}
          </summary>
          <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-bg-secondary p-2 text-xs text-fg-secondary">
            {account.spam_reply}
          </p>
        </details>
      )}
    </>
  );
}

function MaintenanceRow({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const status = useMaintenanceStatus(accountId);
  const toggle = useSetMaintenanceEnabled();
  return (
    <StateRow label={t("accounts.maintenance.title")}>
      {status.data && (
        <span className="mr-2 text-xs text-fg-muted">
          {t("accounts.maintenance.done7d")}: {status.data.done_last_7d}
        </span>
      )}
      <Switch
        checked={status.data?.enabled ?? false}
        onCheckedChange={(enabled) => toggle.mutate({ accountId, enabled })}
        disabled={status.isLoading || toggle.isPending}
        aria-label={t("accounts.maintenance.title")}
      />
    </StateRow>
  );
}

function InboxRow({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const state = useInboxState(accountId);
  const setEnabled = useSetInboxEnabled();
  return (
    <StateRow label={t("accounts.inbox.title")}>
      <Switch
        checked={state.data?.enabled ?? false}
        onCheckedChange={(enabled) => setEnabled.mutate({ accountId, enabled })}
        disabled={state.isLoading || setEnabled.isPending}
        aria-label={t("accounts.inbox.title")}
      />
    </StateRow>
  );
}

function WarmupSection({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const schedule = useWarmupSchedule(account.id);
  const start = useStartWarmup();
  const skip = useSkipWarmup();
  const restart = useRestartWarmup();
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = schedule.data ?? [];
  // Keep-alive steps of a finished warmup share the table. They are not part
  // of the plan: counted in, a completed warmup read "62 of 95" and drifted.
  const planRows = rows.filter((r) => !r.action_params?.maintenance);
  const total = planRows.length;
  const done = planRows.filter((r) => r.status === "done").length;
  const failed = planRows.filter((r) => r.status === "failed").length;
  // "Resume" makes sense when the plan was cut short — or was never made (an
  // account marked warmed by hand): then it starts a real warmup.
  const resumable = planRows.length === 0 || planRows.some((r) => r.status !== "done");
  const nextRow = rows
    .filter((r) => r.status === "pending")
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))[0];

  const onStart = async () => {
    setError(null);
    try {
      await start.mutateAsync(account.id);
      pushToast({ kind: "success", text: t("accounts.warmup.start") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onSkip = async () => {
    setError(null);
    try {
      await skip.mutateAsync(account.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onRestart = async () => {
    setError(null);
    try {
      await restart.mutateAsync(account.id);
      pushToast({ kind: "success", text: t("accounts.warmup.restarted") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{t("accounts.warmup.title")}</p>
          {account.warmup_completed ? (
            <StatusBadge
              domain="account"
              value="active"
              label={t("accounts.warmup.completed")}
            />
          ) : total > 0 ? (
            <StatusBadge
              domain="account"
              value="warming_up"
              label={t("accounts.warmup.in_progress")}
            />
          ) : null}
        </div>

        {account.warmup_completed ? (
          <>
            {total > 0 && (
              <ProgressBar
                value={done}
                max={total}
                tone="success"
                showValue
                label={t("accounts.warmup.summary", { done, total })}
              />
            )}
            {resumable && (
              <>
                <p className="text-xs text-fg-muted">{t("accounts.warmup.restart_hint")}</p>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmRestart(true)}
                    loading={restart.isPending}
                  >
                    {t("accounts.warmup.restart")}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : total > 0 ? (
          <>
            <ProgressBar
              value={done}
              max={total}
              tone={failed > 0 ? "warning" : "accent"}
              showValue
              label={t("accounts.warmup.summary", { done, total })}
            />
            {failed > 0 && (
              <p className="text-xs text-danger">
                {failed} {t("accounts.warmup.failed_short")}
              </p>
            )}
            {nextRow && (
              <p className="text-xs text-fg-muted">
                {t("accounts.warmup.next_at", {
                  time: formatDateTime(nextRow.scheduled_at),
                  action: t(`warmup.action.${nextRow.action_type}`, {
                    defaultValue: nextRow.action_type,
                  }),
                })}
              </p>
            )}
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setConfirmSkip(true)} loading={skip.isPending}>
                {t("accounts.warmup.skip_short")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-fg-muted">{t("accounts.warmup.no_plan_hint")}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmSkip(true)} loading={skip.isPending}>
                {t("accounts.warmup.skip_short")}
              </Button>
              <Button size="sm" onClick={onStart} loading={start.isPending}>
                {t("accounts.warmup.start")}
              </Button>
            </div>
          </>
        )}

        {error && <p className="break-all text-xs text-danger">{error}</p>}
      </CardContent>

      <ConfirmDialog
        open={confirmSkip}
        onOpenChange={setConfirmSkip}
        tone="danger"
        title={t("accounts.warmup.skip_confirm")}
        confirmText={t("accounts.warmup.skip")}
        cancelText={t("common.cancel")}
        onConfirm={onSkip}
      />

      <ConfirmDialog
        open={confirmRestart}
        onOpenChange={setConfirmRestart}
        tone="danger"
        title={t("accounts.warmup.restart_confirm")}
        confirmText={t("accounts.warmup.restart")}
        cancelText={t("common.cancel")}
        onConfirm={onRestart}
      />
    </Card>
  );
}
